// src/components/User/index.js
import React, { useState, useEffect } from 'react';
import EditUserModal from './EditUserModal';
import AddUserForm from './AddUserForm';
import { Table, Button, Input, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { getUsers, createUser, updateUser, deleteUser, getDealers } from '../../services/api';
import toast from 'react-hot-toast';
import './UserManagement.scss';

const UserManagement = ({ setTitle }) => {
  const [users, setUsers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddUserModalVisible, setIsAddUserModalVisible] = useState(false);
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [addUserLoading, setAddUserLoading] = useState(false);

  useEffect(() => {
    setTitle('EarthVerse CRM - 用戶管理');
    fetchAllDealers();
    fetchUsers();
  }, [setTitle]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const fetchedUsers = await getUsers();
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDealers = async () => {
    try {
      const dealers = await getDealers();
      setDealers(dealers || []);
    } catch (error) {
      console.error('Error fetching dealers:', error);
      toast.error('Failed to fetch dealers. Please try again.');
    }
  };

  const handleAddUser = async (userData) => {
    try {
      setAddUserLoading(true);
      const newUser = await createUser(userData);
      setUsers((prevUsers) => [...prevUsers, newUser]);
      toast.success('User added successfully!');
      setIsAddUserModalVisible(false);
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to add user. Please try again.');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      setEditUserLoading(true);
      const updatedUser = await updateUser(userId, userData);
      setUsers((prevUsers) =>
        prevUsers.map((user) => (user.id === userId ? { ...user, ...updatedUser } : user))
      );
      toast.success('User updated successfully!');
      setIsEditModalVisible(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user. Please try again.');
    } finally {
      setEditUserLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId);
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
      toast.success('User deleted successfully!');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user. Please try again.');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setIsEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setEditingUser(null);
  };

  const color = (level) => {
    if (level === 0) {
      return 'blue';
    } else if (level === -1) {
      return 'red';
    } else {
      return 'inherit';
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Dealer',
      dataIndex: 'dealer_name',
      key: 'dealer_name',
      sorter: (a, b) => (a.dealer_name || '').localeCompare(b.dealer_name || ''),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => (
        <span style={{ color: color(record.level) }}>{name}</span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
      render: (email, record) => (
        <span style={{ color: color(record.level) }}>{email}</span>
      )
    },
    {
      title: '目前下載量 / 限制下載量',
      dataIndex: 'download_limit',
      key: 'download',
      render: (download_limit, record) => {
        const download_size = record.download_size;
    
        return `${download_size} GB / ${download_limit} GB`;
      },
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      sorter: (a, b) => {
        const dateA = a.last_login ? new Date(a.last_login) : new Date(0);
        const dateB = b.last_login ? new Date(b.last_login) : new Date(0);
        return dateA - dateB;
      },
      render: (last_login) => {
        if (!last_login) {
          return '-';
        }
        if (last_login < 2024) {
          return '-';
        }
        return new Date(last_login).toLocaleString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} style={{ marginRight: 8 }} />
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </span>
      ),
    },
  ];

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const footer = () => (
    <div>
      <p><b>Total Users:</b> {users.length}</p>
      <p style={{ color: 'blue' }}>藍色→此帳號為管理員權限，有管理員權限才能進到後台</p>
      <p style={{ color: 'red' }}>紅色→此帳號已被停用</p>
    </div>
  );

  return (
    <div className="user-list-container">
      <h2>用戶管理</h2>
      <div className="user-actions" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Input
          placeholder="Search users..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: 200, marginBottom: 16 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsAddUserModalVisible(true)}
          style={{ marginBottom: 16, marginLeft: 16 }}
        >
          Add New User
        </Button>
      </div>
      <Table
        dataSource={filteredUsers}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 30 }}
        loading={loading}
        footer={footer}
      />
      <AddUserForm
        visible={isAddUserModalVisible}
        onCancel={() => setIsAddUserModalVisible(false)}
        onAddUser={handleAddUser}
        loading={addUserLoading}
        dealers={dealers}
      />
      <EditUserModal
        visible={isEditModalVisible}
        onCancel={handleEditCancel}
        onSave={handleUpdateUser}
        user={editingUser || {}}
        loading={editUserLoading}
        dealers={dealers}
      />
    </div>
  );
};

export default UserManagement;